import { useEffect, useRef, useState } from "react";

/**
 * Interactive Toulios brand mark for page heroes.
 *
 * variant="full" (default): full lockup PNG — for the Home hero.
 * variant="mark":          smaller TA monogram only — for subpage heroes.
 *
 * Behavior is identical across variants: fade + scale entrance, screen-blend
 * dark dropout, sheen sweep, hover lift + glow (desktop only), scroll parallax.
 * Touch + reduced-motion users get the static settled state.
 */
export default function HeroBrandMark({ className = "", variant = "full" }) {
  const wrapRef = useRef(null);
  const [armed, setArmed] = useState(false);
  const [parallax, setParallax] = useState(0);

  const src = variant === "mark" ? "/logo-mark.png" : "/hero-ta.png";
  const maxW = variant === "mark" ? 320 : 520;

  useEffect(() => {
    const t = setTimeout(() => setArmed(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const noHover = window.matchMedia("(hover: none)").matches;
    if (reducedMotion || noHover) return;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const node = wrapRef.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        const clamped = Math.max(-1, Math.min(1, progress));
        setParallax(-clamped * 14);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="hero-brand-mark"
      aria-hidden="true"
      className={`hero-mark ${armed ? "hero-mark--in" : ""} ${className}`}
      style={{ transform: `translate3d(0, ${parallax}px, 0)`, maxWidth: `${maxW}px` }}
    >
      <span className="hero-mark__glow" />
      <img
        src={src}
        alt=""
        className="hero-mark__img"
        draggable={false}
      />
      <span className="hero-mark__sheen" />

      <style>{`
        .hero-mark {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          opacity: 0;
          transition: opacity 900ms ease, transform 600ms ease-out;
          will-change: transform;
        }
        .hero-mark--in { opacity: 1; }

        .hero-mark__glow {
          position: absolute;
          inset: -6%;
          background: radial-gradient(closest-side, rgba(212,175,55,0.22), transparent 70%);
          filter: blur(2px);
          z-index: 0;
          transition: background 500ms ease, transform 500ms ease;
        }

        .hero-mark__img {
          position: relative;
          z-index: 1;
          width: 100%;
          height: 100%;
          object-fit: contain;
          mix-blend-mode: screen;
          transform: scale(0.95);
          transition: transform 900ms cubic-bezier(0.22, 1, 0.36, 1), filter 500ms ease;
          filter: drop-shadow(0 6px 18px rgba(0,0,0,0.4));
          user-select: none;
          -webkit-user-drag: none;
          image-rendering: -webkit-optimize-contrast;
        }
        .hero-mark--in .hero-mark__img {
          transform: scale(1);
        }

        .hero-mark__sheen {
          position: absolute;
          top: 0;
          left: -40%;
          width: 40%;
          height: 100%;
          background: linear-gradient(
            105deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.10) 50%,
            rgba(255,255,255,0) 100%
          );
          mix-blend-mode: screen;
          pointer-events: none;
          z-index: 2;
          opacity: 0;
        }
        .hero-mark--in .hero-mark__sheen {
          animation: hero-mark-sheen 1600ms ease-out 800ms 1 forwards;
        }
        @keyframes hero-mark-sheen {
          0%   { left: -40%; opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { left: 110%; opacity: 0; }
        }

        @media (hover: hover) and (pointer: fine) {
          .hero-mark:hover .hero-mark__img {
            transform: translateY(-6px) scale(1.025);
            filter: drop-shadow(0 12px 24px rgba(212,175,55,0.22));
          }
          .hero-mark:hover .hero-mark__glow {
            background: radial-gradient(closest-side, rgba(212,175,55,0.32), transparent 70%);
            transform: scale(1.04);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-mark, .hero-mark__img, .hero-mark__glow {
            transition: none;
            opacity: 1;
            transform: none;
          }
          .hero-mark__sheen { display: none; }
        }
      `}</style>
    </div>
  );
}
